import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { treeById, type ConversationTree, type TreeNode } from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { useI18n, type I18nKey } from '../../i18n';
import { AstrallyMark } from '../../components/AstrallyMark';
import { Bubble } from './AllyBubble';
import { ChatList } from './ChatList';
import { TreePicker, useTreeProgress } from './TreePicker';
import { askTextFor, groundingFor, stepAction, useAllySuggestionChips } from './suggestions';

/** Greeting keys exist so this line translates; it used to be a literal "Namaste". */
function greetingKey(hour: number): I18nKey {
  if (hour < 12) return 'home.greeting.morning';
  if (hour < 17) return 'home.greeting.afternoon';
  if (hour < 22) return 'home.greeting.evening';
  return 'home.greeting.night';
}

export function AllyScreen() {
  const { t } = useI18n();
  const app = useApp();
  const [draft, setDraft] = useState('');
  const [openSources, setOpenSources] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const suggestions = useAllySuggestionChips();

  // --- the four conversation trees -----------------------------------------
  const trees = useTreeProgress();
  /** Which path is expanded, or null for the four-card overview. */
  const [activeTreeId, setActiveTreeId] = useState<string | null>(null);
  /** True once the reader has asked for the paths inside a running chat. */
  const [treesOpen, setTreesOpen] = useState(false);
  /** The beat whose answer the composer is currently collecting. */
  const [answering, setAnswering] = useState<{ treeId: string; nodeId: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  /** The conversation on screen. Null before the first message is ever sent. */
  const activeChat = app.chats.find((c) => c.id === app.activeChatId) ?? null;

  const socialEnabled = app.user?.socialEnabled === true;

  /**
   * Home deep-links `/ai?tree=<id>` from its "continue your path" card.
   *
   * The param is consumed and stripped: it is an instruction to open a path
   * once, not a property of this screen. Left in place, a refresh — or a back
   * navigation onto a conversation that has since moved on — would reopen the
   * picker over a live chat. Unknown ids are ignored rather than trusted.
   */
  useEffect(() => {
    const id = searchParams.get('tree');
    if (!id) return;
    if (treeById(id)) {
      setActiveTreeId(id);
      setTreesOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('tree');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Home can start a send and navigate here mid-flight, so the pending state
  // lives in AppState — this screen only renders the shared chat history.
  const busy = app.allyBusy;

  // Also runs when the active chat changes, so opening an older conversation
  // lands at its last message rather than at the top of the scroll.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [app.chatHistory.length, app.activeChatId, busy]);

  const send = async (text: string, modelClass: 'standard' | 'deeper' | 'master' = 'standard') => {
    const clean = text.trim();
    if (!clean || busy) return;
    setDraft('');
    // A beat of kind 'question' is answered by whatever the reader sends next
    // — which is exactly what the beat card promises — so the pending beat is
    // captured here and only marked done if the send actually goes through.
    const pending = answering;
    // sendToAlly only ever rejects on its own busy guard — AI failures come
    // back as an ally message. A double tap can beat this render's `busy`, so
    // swallow that rejection rather than leaving it unhandled.
    const entry = await app.sendToAlly(clean, modelClass).catch(() => null);
    if (entry && pending) {
      const tree = treeById(pending.treeId);
      if (tree) await trees.complete(tree, pending.nodeId);
      setAnswering(null);
    }
  };

  /**
   * A beat was actioned. WHAT IT COSTS IS DECIDED HERE, not in the picker:
   *
   *   - 'open'   — a gate. Local bookkeeping, no model call, no cost.
   *   - 'answer' — Ally asked; the composer collects the reply. Nothing is
   *                sent until the reader types and presses send themselves.
   *   - 'ask'    — the only branch that reaches the model, and only from this
   *                deliberate tap. Nothing here ever runs on mount, on opening
   *                a path, or on focusing a beat.
   */
  const stepTree = async (tree: ConversationTree, node: TreeNode) => {
    const action = stepAction(node);
    if (action.mode === 'open') {
      await trees.complete(tree, node.id);
      return;
    }
    if (action.mode === 'answer') {
      setAnswering({ treeId: tree.id, nodeId: node.id });
      return;
    }
    if (busy) return;
    setAnswering(null);
    const entry = await app
      .sendToAlly(askTextFor(tree, node, groundingFor(node, app.facts)))
      .catch(() => null);
    // Only a reply that arrived marks the beat walked — a refused or failed
    // generation must not advance the path past a question never answered.
    if (entry) await trees.complete(tree, node.id);
  };

  const costs = app.entitlement?.config.creditCosts;

  /** "Go Deeper" re-asks the last question at the Sonnet-class tier. */
  const goDeeper = async () => {
    const lastUser = [...app.chatHistory].reverse().find((e) => e.role === 'user');
    if (!lastUser || !costs) return;
    if (!confirm(t('ai.cost.confirm').replace('{n}', String(costs.deeperAnswer)))) return;
    await send(lastUser.text, 'deeper');
  };

  /** Master Reading — the Opus-class flagship, double-confirmed. */
  const masterReading = async () => {
    if (!costs) return;
    if (!confirm(t('ai.cost.confirm').replace('{n}', String(costs.masterReading)))) return;
    if (!confirm(t('ai.master.confirm2').replace('{n}', String(costs.masterReading)))) return;
    await send(
      'Please give me a Master Reading: a comprehensive, multi-system life reading covering my personality, emotional patterns, career direction, relationships and current timing — grounded in my calculated Jyotish and Western charts.',
      'master',
    );
  };

  const greeting = useMemo(() => t(greetingKey(new Date().getHours())), [t]);

  /**
   * The paths are on screen for an empty chat — the moment someone has no idea
   * what to ask — and stay reachable afterwards through the chip below, so a
   * path can still be walked beat by beat once the conversation has started.
   * Waiting for `loaded` avoids one frame where a walked path reads as new.
   */
  const treesVisible = trees.loaded && (app.chatHistory.length === 0 || treesOpen);

  /**
   * Quick replies belong under ALLY's last line, never under the reader's own.
   * An empty chat counts: nothing has been said, so the openers are the only
   * thing on offer. After a user message the row disappears — the reader has
   * already chosen what to ask, and re-offering four alternatives at that
   * moment reads as the app not having listened.
   */
  const lastEntry = app.chatHistory[app.chatHistory.length - 1];
  const showQuickReplies = !lastEntry || lastEntry.role === 'ally';

  return (
    <div className="screen screen--flush" style={{ paddingBottom: 'calc(132px + var(--safe-bottom))' }}>
      <div className="spread" style={{ marginBottom: 10, gap: 8 }}>
        <div className="row" style={{ minWidth: 0 }}>
          <button
            className="btn btn--ghost btn--small"
            aria-label={t('chats.open', { n: app.chats.length })}
            style={{ flexShrink: 0 }}
            onClick={() => setListOpen(true)}
          >
            <span aria-hidden>☰</span>
            {app.chats.length > 1 && (
              <span className="tiny" style={{ marginLeft: 6 }}>
                {app.chats.length}
              </span>
            )}
          </button>
          <div className="avatar" style={{ flexShrink: 0 }}>
            <AstrallyMark size={24} lagnaColor={null} weight={5} />
          </div>
          <div style={{ minWidth: 0 }}>
            {/* The chat's own name is the heading once there is one — that is
                the only signal that these are separate conversations. */}
            <div
              className="h2"
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {activeChat ? activeChat.title : t('tabs.ai')}
            </div>
            <div className="tiny faint">{t('ally.grounding')}</div>
          </div>
        </div>
        {/* No balance chip: a wallet in the chat header made every question
            feel metered. Standard chat never spends credits; the paid tiers
            state their price in their own confirm step, where it matters. */}
        <button
          className="btn btn--ghost btn--small"
          aria-label={t('chats.newAria')}
          title={t('chats.newAria')}
          style={{ flexShrink: 0 }}
          onClick={() => void app.newChat()}
        >
          <span aria-hidden>＋</span>
        </button>
      </div>

      <ChatList open={listOpen} onClose={() => setListOpen(false)} />

      {/*
        Which astrologer you are talking to. Shown only while the chat is
        empty: a conversation has one voice, and switching mid-thread would
        leave earlier replies contradicting their own vocabulary. Jyotish is
        the default and comes first — Western is the opt-in side path, and the
        tradition colours say so without needing a label to explain it.
      */}
      {app.chatHistory.length === 0 && (
        <div className="row" style={{ marginBottom: 12, gap: 8 }}>
          {(['jyotish', 'western'] as const).map((p) => {
            const on = (activeChat?.persona ?? 'jyotish') === p;
            return (
              <button
                key={p}
                data-mode={p === 'jyotish' ? 'vedic' : 'western'}
                className={`chip ${on ? 'chip--on' : ''}`}
                aria-pressed={on}
                style={on ? { borderColor: 'var(--mode)', color: 'var(--mode-ink)' } : undefined}
                onClick={() => void app.newChat(p)}
              >
                {on && <span aria-hidden>✓ </span>}
                {t(p === 'jyotish' ? 'chats.personaJyotish' : 'chats.personaWestern')}
              </button>
            );
          })}
        </div>
      )}

      {/*
        The four conversation trees. Everything visible here — the cards, the
        map, Ally's line at each beat and its grounding — is authored copy plus
        values the engine already computed, so opening a path costs nothing.
        Only `stepTree`'s 'ask' branch and the reader's own typed answer ever
        reach the model, and each needs a deliberate tap.
      */}
      {treesVisible && (
        <div style={{ marginBottom: 14 }}>
          <div className="spread" style={{ marginBottom: 8 }}>
            <div className="section-title" style={{ margin: 0 }}>
              {t('trees.title')}
            </div>
            {app.chatHistory.length > 0 && (
              <button
                className="btn btn--ghost btn--small"
                onClick={() => {
                  setTreesOpen(false);
                  setActiveTreeId(null);
                }}
              >
                {t('trees.hide')}
              </button>
            )}
          </div>
          <TreePicker
            progress={trees.progress}
            activeTreeId={activeTreeId}
            onSelect={setActiveTreeId}
            onStep={(tree, node) => void stepTree(tree, node)}
            onRestart={(tree) => void trees.restart(tree)}
            busy={busy}
            socialEnabled={socialEnabled}
            answering={answering}
            onCancelAnswer={() => setAnswering(null)}
            facts={app.facts}
          />
        </div>
      )}

      {app.backend.kind === 'demo' && (
        <div className="banner banner--info small" style={{ marginBottom: 12 }}>
          {t('common.demoMode')}
        </div>
      )}

      <div className="chat">
        {app.chatHistory.length === 0 && (
          <div className="card fade-in">
            <div className="h3" style={{ marginBottom: 6 }}>
              {greeting}{app.user ? `, ${app.user.displayName}` : ''}
            </div>
            <div className="muted small" style={{ lineHeight: 1.55 }}>
              Your chart is calculated and I can see{' '}
              {app.facts.length} grounded facts about it. Ask me anything — or start from one of
              these:
            </div>
            {/* Deleting the last chat, or starting a new one, lands here. When
                older conversations still exist, say so — an empty screen with
                no way back would read as lost history. */}
            {app.chats.some((c) => c.messageCount > 0) && (
              <button className="chip" style={{ marginTop: 12 }} onClick={() => setListOpen(true)}>
                <span aria-hidden>☰ </span>
                {t('chats.earlier')}
              </button>
            )}
          </div>
        )}

        {app.chatHistory.map((entry, i) => (
          <Bubble
            key={i}
            entry={entry}
            onAction={app.actOnRecommendation}
            openSources={openSources}
            setOpenSources={setOpenSources}
            factLookup={(id) => app.facts.find((f) => f.id === id)}
          />
        ))}

        {busy && (
          <div className="bubble-row bubble-row--ally fade-in">
            <span className="bubble-avatar" aria-hidden />
            <div className="bubble bubble--ally">
              {/* The dots ARE the message, so the sentence they replace has to
                  reach a screen reader some other way. */}
              <span className="typing" role="status" aria-label={t('home.aiThinking')}>
                <span className="typing__dot" aria-hidden />
                <span className="typing__dot" aria-hidden />
                <span className="typing__dot" aria-hidden />
              </span>
            </div>
          </div>
        )}

        {/*
          QUICK REPLIES, hung under the last thing Ally said — which is exactly
          the moment someone does not know what to ask next. Shown on an empty
          chat and after any Ally turn; never while a turn is in flight, and
          never under the reader's own message.

          They cost nothing to RENDER: the list is derived from the grounding
          fact pack the engine already computed. Only the tap spends anything,
          and the tap is `send`, the same path as typing the question by hand.
        */}
        {!busy && showQuickReplies && (
          <div className="chat-suggest wrap" data-testid="ally-suggestions">
            {suggestions.map((s) => (
              <button
                key={s.text}
                className="chip chip--suggest"
                onClick={() => void send(s.text)}
              >
                <span aria-hidden>{s.emoji}</span>
                {s.text}
              </button>
            ))}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {!treesVisible && (
        <div className="wrap" style={{ marginTop: 14 }}>
          {/* The way back into the paths once the chat has started. Opening the
              picker is free, so this chip never spends a message. */}
          <button className="chip" onClick={() => setTreesOpen(true)}>
            <span aria-hidden>◇ </span>
            {t('trees.open')}
          </button>
        </div>
      )}

      {!busy && app.chatHistory.length > 0 && app.chatHistory[app.chatHistory.length - 1].role === 'ally' && costs && (
        <div className="wrap" style={{ marginTop: 12 }}>
          <button className="chip chip--on" onClick={goDeeper} title={t('ai.deeper.desc')}>
            ◇ {t('ai.deeper')} · {costs.deeperAnswer} ◈
          </button>
          <button className="chip" onClick={masterReading} title={t('ai.master.desc')}>
            ◆ {t('ai.master')} · {costs.masterReading} ◈
          </button>
        </div>
      )}

      <div className="composer">
        <input
          className="input"
          placeholder={answering ? t('trees.answerPlaceholder') : t('ally.placeholder')}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send(draft)}
          disabled={busy}
        />
        <button
          className="btn btn--primary"
          aria-label={t('home.askAstrally')}
          onClick={() => send(draft)}
          disabled={busy || !draft.trim()}
        >
          ↑
        </button>
      </div>
    </div>
  );
}
